import { useCallback } from 'react'
import { useTranslation } from '../../i18n'
import type { ReplayParticipant } from '../../lib/replayTypes'

/**
 * The name the replay shows for a carrier, wherever it shows one: the map chip,
 * the filter lists, a chat bubble.
 *
 * An anonymous carrier has no name to show, so it reads as "Team member" or, for
 * a dog, "Dog". Every surface goes through this one function so that a new place
 * that names people cannot forget the anonymous case and print an empty label.
 */
export function useReplayParticipantName(): (participant: ReplayParticipant) => string {
  const { t } = useTranslation('replay')
  return useCallback(
    (participant: ReplayParticipant) => {
      if (participant.anonymous !== true) return participant.name
      return participant.role === 'dog' ? t('anonymousDog') : t('anonymousMember')
    },
    [t],
  )
}

/**
 * The participants a viewer starts with deselected: the anonymous ones, as the
 * shared-replay design has them. Their trail is still there to tick on.
 */
export function initiallyHiddenParticipants(
  participants: readonly ReplayParticipant[],
): ReadonlySet<string> {
  return new Set(participants.filter((p) => p.anonymous === true).map((p) => p.id))
}
